import { useEffect, useRef, useState } from 'react';
import { IoIosRadioButtonOff, IoIosRadioButtonOn } from 'react-icons/io';
import { IOrder, IPaymentMethod, PaymentOptionProps } from './type';
import { ORDER_STATUS, PAYMENT_GATEWAY } from './constants';
import { createOrder, createOrderSDK, findOrderByRefId, getSdkPaymentMethods } from './core/payment';
import PaymentSuccess from './components/payment/success';
import PaymentCanceled from './components/payment/cancel';
import { buildUrl, showError } from './core/utils';
import { getPaymentCloseUrl } from './core/close-payment-window';
import { getEffectiveAccessToken } from './core/sdk-auth';


export default function Payment({
  baseUrl,
  type,
  productId,
  accessToken,
  apiEndpoint,
  onPaymentError,
  onPaymentSuccess,
  serviceHref,
  privacyHref,
  skipLogin,
  deviceId,
  platform,
  appsflyerId,
  userId: optionUserId,
  ext: optionExt,
  charId,
  charName,
  serverId,
  serverName,
  packageName,
  price,
  googleProductId,
  goodids,
  onClickClose = () => { },
  standalonePayment = false,
}: PaymentOptionProps) {
  const authToken = getEffectiveAccessToken(accessToken);
  const [gateway, setGateway] = useState(PAYMENT_GATEWAY.PAY_OS);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const gatewayPollAbortRef = useRef<AbortController | null>(null);
  /** Popup MoMo/Zalo do window.open — đóng từ H5 khi nhận hủy/success (ổn định hơn popup tự close). */
  const paymentPopupRef = useRef<Window | null>(null);
  /** true = H5 chủ động đóng popup (không coi là user hủy). */
  const intentionalPopupCloseRef = useRef(false);
  /** refId đang chờ cổng (poll / quay lại từ app MoMo trên iOS). */
  const pendingGatewayRefIdRef = useRef<string | null>(null);
  const [order, setOrder] = useState<any>();
  const [methods, setMethods] = useState<IPaymentMethod[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [awaitingGateway, setAwaitingGateway] = useState(false);
  /** Cổng (Zalo/MoMo) báo success — hiện UI thành công dù order chưa PAID (chờ IPN). */
  const [gatewayTrustedSuccess, setGatewayTrustedSuccess] = useState(false);

  const character = {
    charId: `${charId || ''}`,
    charName: charName || '',
    serverId: `${serverId || ''}`,
    serverName: serverName || '',
  };

  /** Resolve ext: openPaymentModal options → URL ?ext= */
  const resolveExt = () => {
    if (optionExt != null && optionExt !== '') return optionExt;
    const urlExt = new URLSearchParams(window.location.search).get('ext');
    if (!urlExt) return undefined;
    const trimmed = urlExt.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}'))
      || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try { return JSON.parse(trimmed); } catch { return trimmed; }
    }
    return trimmed;
  };

  /** Resolve userId: options → URL ?userId= */
  const resolveUserId = () => {
    if (optionUserId != null && String(optionUserId).trim() !== '') {
      return String(optionUserId).trim();
    }
    return new URLSearchParams(window.location.search).get('userId') || '';
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoadingMethods(true);
        const { data } = await getSdkPaymentMethods(apiEndpoint);
        setMethods(data || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch payment methods:', e);
        setMethods([]);
      } finally {
        setLoadingMethods(false);
      }
    };
    fetchPaymentMethods();
  }, [apiEndpoint]);

  const onClose = () => {
    gatewayPollAbortRef.current?.abort();
    pendingGatewayRefIdRef.current = null;
    try {
      paymentPopupRef.current?.close();
    } catch {
      /* ignore */
    }
    paymentPopupRef.current = null;
    setOrder(null);
    onClickClose();
  };

  const closePaymentPopup = () => {
    intentionalPopupCloseRef.current = true;
    try {
      paymentPopupRef.current?.close();
    } catch {
      /* ignore */
    }
    paymentPopupRef.current = null;
  };

  /**
   * iOS Safari: window.open sau await bị chặn.
   * Phải gọi synchronously trong click (trước createOrder).
   */
  const openGatewayPopupPlaceholder = (): Window | null => {
    if (standalonePayment) return null;
    intentionalPopupCloseRef.current = false;
    const popup = window.open('about:blank', 'PaymentGatewayProcess', 'width=2500,height=2500,scrollbars=yes');
    paymentPopupRef.current = popup;
    if (popup) {
      try {
        popup.document.write('<!doctype html><title>Đang chuyển…</title><body style="font-family:sans-serif;padding:24px;text-align:center">Đang chuyển tới cổng thanh toán…</body>');
        popup.document.close();
      } catch {
        /* ignore cross-origin / write failures */
      }
      try {
        popup.focus();
      } catch {
        /* ignore */
      }
    }
    return popup;
  };

  const redirectToPaymentGateway = (paymentUrl: string, refId?: string, existingPopup?: Window | null) => {
    if (standalonePayment) {
      const closeUrl = getPaymentCloseUrl();
      window.location.href = buildUrl(`${baseUrl}/sdk-payment-redirect`, {
        paymentUrl,
        ...(refId ? { refId } : {}),
        ...(closeUrl ? { closeUrl } : {}),
      });
      return;
    }
    intentionalPopupCloseRef.current = false;
    let popup = existingPopup ?? paymentPopupRef.current;
    if (popup && !popup.closed) {
      try {
        popup.location.href = paymentUrl;
        paymentPopupRef.current = popup;
        return;
      } catch {
        /* fall through — mở lại */
      }
    }
    // Fallback: cố mở lại (có thể bị Safari chặn nếu không còn user gesture)
    popup = window.open(paymentUrl, 'PaymentGatewayProcess', 'width=2500,height=2500,scrollbars=yes');
    paymentPopupRef.current = popup;
    if (!popup) {
      // Popup bị chặn hoàn toàn → điều hướng tab hiện tại
      window.location.href = paymentUrl;
    }
  };

  /** Popup đã đóng mà chưa có kết quả → coi như hủy (user đóng tab MoMo). */
  useEffect(() => {
    if (!awaitingGateway || standalonePayment) return undefined;
    const timer = window.setInterval(() => {
      const popup = paymentPopupRef.current;
      if (!popup || !popup.closed) return;
      if (intentionalPopupCloseRef.current) {
        paymentPopupRef.current = null;
        return;
      }
      window.clearInterval(timer);
      paymentPopupRef.current = null;
      window.setTimeout(() => {
        if (intentionalPopupCloseRef.current) return;
        if (!gatewayPollAbortRef.current) return;
        gatewayPollAbortRef.current.abort();
        gatewayPollAbortRef.current = null;
        pendingGatewayRefIdRef.current = null;
        setAwaitingGateway(false);
        setOrder((prev: any) => prev || { id: '', status: 'created' });
        onPaymentError({ message: 'Thanh toán đã hủy' });
      }, 800);
    }, 400);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingGateway, standalonePayment]);


  /** Xác nhận đơn sau cổng — dùng khi có postMessage hoặc khi mất opener (poll). */
  const settleGatewayOrder = async (refId: string, gatewayOk: boolean, signal?: AbortSignal) => {
    const sleep = (ms: number) => new Promise<void>((r, reject) => {
      const t = setTimeout(r, ms);
      signal?.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new DOMException('aborted', 'AbortError'));
      }, { once: true });
    });

    const finishPaid = (respOrder: IOrder) => {
      closePaymentPopup();
      pendingGatewayRefIdRef.current = null;
      setGatewayTrustedSuccess(true);
      setOrder(respOrder);
      onPaymentSuccess(respOrder);
      setAwaitingGateway(false);
    };

    const finishFailed = (respOrder: any) => {
      closePaymentPopup();
      pendingGatewayRefIdRef.current = null;
      setOrder(respOrder);
      onPaymentError(respOrder || { refId, message: 'Thanh toán thất bại' });
      setAwaitingGateway(false);
    };

    try {
      setAwaitingGateway(true);
      pendingGatewayRefIdRef.current = refId;
      let respOrder: IOrder = (await findOrderByRefId(refId, apiEndpoint, authToken)).data;
      if (signal?.aborted) return;

      if (gatewayOk && (respOrder as any)?.paymentGatewayStatus !== 'failed') {
        finishPaid(respOrder);
        for (let i = 0; i < 10; i += 1) {
          if (signal?.aborted) return;
          if (respOrder?.status === ORDER_STATUS.PAID) break;
          if ((respOrder as any)?.paymentGatewayStatus === 'failed') break;
          await sleep(2000);
          respOrder = (await findOrderByRefId(refId, apiEndpoint, authToken)).data;
          setOrder(respOrder);
          if (respOrder?.status === ORDER_STATUS.PAID) {
            onPaymentSuccess(respOrder);
          }
        }
        return;
      }

      // Poll dài — iPhone mở app MoMo thường không redirect lại popup; dựa IPN/PAID rồi đóng popup từ H5
      const maxAttempts = gatewayOk ? 10 : 100;
      for (let i = 0; i < maxAttempts; i += 1) {
        if (signal?.aborted) return;
        if (respOrder?.status === ORDER_STATUS.PAID) {
          finishPaid(respOrder);
          return;
        }
        if ((respOrder as any)?.paymentGatewayStatus === 'failed') {
          finishFailed(respOrder);
          return;
        }
        await sleep(gatewayOk ? 2000 : 3000);
        respOrder = (await findOrderByRefId(refId, apiEndpoint, authToken)).data;
      }
      if (signal?.aborted) return;
      if (respOrder?.status === ORDER_STATUS.PAID) {
        finishPaid(respOrder);
      } else if ((respOrder as any)?.paymentGatewayStatus === 'failed') {
        finishFailed(respOrder);
      } else if (!gatewayOk) {
        // Vẫn chờ — giữ awaiting; user đóng modal thì abort
        return;
      } else {
        finishFailed(respOrder || { refId, message: 'Thanh toán chưa hoàn tất' });
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      showError(e?.response?.data || e);
      onPaymentError(e?.response?.data || e);
    } finally {
      if (!signal?.aborted) setAwaitingGateway(false);
    }
  };

  const startGatewayPoll = (refId: string) => {
    gatewayPollAbortRef.current?.abort();
    const ac = new AbortController();
    gatewayPollAbortRef.current = ac;
    pendingGatewayRefIdRef.current = refId;
    void settleGatewayOrder(refId, false, ac.signal);
  };

  /** iOS: user quay lại Safari từ app MoMo → kiểm tra đơn ngay + đóng popup nếu đã PAID. */
  useEffect(() => {
    if (standalonePayment) return undefined;

    const checkOnResume = async () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      const refId = pendingGatewayRefIdRef.current;
      if (!refId) return;
      try {
        const respOrder = (await findOrderByRefId(refId, apiEndpoint, authToken)).data;
        if (respOrder?.status === ORDER_STATUS.PAID) {
          gatewayPollAbortRef.current?.abort();
          gatewayPollAbortRef.current = null;
          closePaymentPopup();
          pendingGatewayRefIdRef.current = null;
          setGatewayTrustedSuccess(true);
          setOrder(respOrder);
          onPaymentSuccess(respOrder);
          setAwaitingGateway(false);
        } else if ((respOrder as any)?.paymentGatewayStatus === 'failed') {
          gatewayPollAbortRef.current?.abort();
          gatewayPollAbortRef.current = null;
          closePaymentPopup();
          pendingGatewayRefIdRef.current = null;
          setOrder(respOrder);
          onPaymentError(respOrder);
          setAwaitingGateway(false);
        }
      } catch {
        /* ignore */
      }
    };

    document.addEventListener('visibilitychange', checkOnResume);
    window.addEventListener('pageshow', checkOnResume);
    window.addEventListener('focus', checkOnResume);
    return () => {
      document.removeEventListener('visibilitychange', checkOnResume);
      window.removeEventListener('pageshow', checkOnResume);
      window.removeEventListener('focus', checkOnResume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint, authToken, standalonePayment]);

  useEffect(() => {
    const handleEvent = async (event: MessageEvent) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;
      const refId = data.refId || data.orderId;
      if (!refId) return;
      if (data.type && data.type !== 'joygames-payment-complete') return;

      // Hủy / thất bại từ portal handoff → đóng popup MoMo ngay + hiện UI hủy (không poll dài)
      if (data.type === 'joygames-payment-complete' && data.success === false) {
        gatewayPollAbortRef.current?.abort();
        gatewayPollAbortRef.current = null;
        pendingGatewayRefIdRef.current = null;
        closePaymentPopup();
        setAwaitingGateway(false);
        try {
          const respOrder = (await findOrderByRefId(String(refId), apiEndpoint, authToken)).data;
          setOrder(respOrder);
          onPaymentError(respOrder || { refId, message: 'Thanh toán đã hủy' });
        } catch {
          setOrder({ id: refId, status: 'created' });
          onPaymentError({ refId, message: 'Thanh toán đã hủy' });
        }
        return;
      }

      const gatewayOk = data.type === 'joygames-payment-complete'
        ? data.success !== false
        : data.status === '1' || data.status === '0' || data.resultCode === '0' || data.resultCode === '1';

      gatewayPollAbortRef.current?.abort();
      const ac = new AbortController();
      gatewayPollAbortRef.current = ac;
      closePaymentPopup();
      await settleGatewayOrder(String(refId), gatewayOk, ac.signal);
    };
    window.addEventListener('message', handleEvent);
    return () => {
      window.removeEventListener('message', handleEvent);
      gatewayPollAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='modal_wrapper' id="payment-modal">
      <button type="button" className='close-btn' onClick={onClose}>
        x
      </button>
      {!order ? <>
        <div className="sm-container">
          {awaitingGateway ? (
            <div style={{ textAlign: 'center', padding: '24px 8px' }}>
              Đang xác nhận thanh toán...
            </div>
          ) : (
            <>
          <div className={"box"}>
            <p>Tên nhân vật: {charName}</p>
            <p>Server: {serverName}</p>
            <p>Gói nạp: {packageName}</p>
          </div>
          <h3 className={"section_ttl"}>Phương thức thanh toán</h3>
          {loadingMethods && <div style={{ textAlign: 'center', padding: '20px' }}>Đang tải phương thức thanh toán...</div>}
          {!loadingMethods && methods.map((m: IPaymentMethod) => (
            <div
              key={m.value}
              aria-hidden
              onClick={() => {
                !submitting && setGateway(m.value);
              }}
              className={`pm_method_select ${gateway === m.value ? 'active' : ''}`}
            >
              <span className={"img_group"}>
                <img height={40} width={40} style={{ objectFit: 'contain', display: 'block' }} src={m.image} alt={m.label} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="pm_method_label" dangerouslySetInnerHTML={{ __html: m.label }} />
                  {m.description && <span className='description'>{m.description}</span>}
                </div>
              </span>
              <span className={"radio"}>
                {gateway === m.value ? <IoIosRadioButtonOn /> : <IoIosRadioButtonOff />}
              </span>
            </div>
          ))}
          <div className={"btn_grp"}>
            <button
              type="button"
              className={"primary_btn"}
              disabled={submitting}
              onClick={async () => {
                if (submittingRef.current) return;
                submittingRef.current = true;

                // iOS Safari: mở popup TRƯỚC await — sau API sẽ bị chặn popup
                const needsGatewayPopup = !standalonePayment
                  && gateway !== PAYMENT_GATEWAY.BALANCE;
                const gatewayPopup = needsGatewayPopup
                  ? openGatewayPopupPlaceholder()
                  : null;

                try {
                  setSubmitting(true);
                  const claimUserId = resolveUserId();
                  const ext = resolveExt();
                  if (gateway === PAYMENT_GATEWAY.BALANCE) {
                    let tokenUserId = '';
                    try {
                      const part = (authToken || '').split('.')[1];
                      if (part) {
                        const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
                        tokenUserId = json?.userId != null ? String(json.userId) : '';
                      }
                    } catch { /* ignore */ }
                    if (!claimUserId || !tokenUserId || claimUserId !== tokenUserId) {
                      showError('Ví xu không khớp với người nạp');
                      return;
                    }
                  }
                  const payload = {
                    type: type || 'h5',
                    productId: productId,
                    item: {
                      name: packageName,
                      googleProductId,
                      price: Math.round(Number(price)),
                      ...(goodids != null ? { goodids } : {}),
                    },
                    platform: platform,
                    appsflyerId: appsflyerId,
                    paymentGateway: gateway,
                    userId: claimUserId || undefined,
                    serverId: `${serverId || ''}`,
                    charId: `${charId || ''}`,
                    serverName: serverName || '',
                    charName: charName || '',
                    ...(ext != null ? { ext } : {}),
                  };
                  const { data } = skipLogin
                    ? await createOrderSDK(payload, apiEndpoint, authToken, productId, deviceId || '')
                    : await createOrder(payload, apiEndpoint, authToken);
                  if (gateway === PAYMENT_GATEWAY.BALANCE) {
                    const paidOrder = data.order?.status === ORDER_STATUS.PAID
                      ? data.order
                      : (await findOrderByRefId(data.order.id, apiEndpoint, authToken)).data;
                    setOrder(paidOrder);
                    onPaymentSuccess(paidOrder);
                    return;
                  }
                  if (gateway === PAYMENT_GATEWAY.PAYER_MAX) {
                    const verifyUrl = buildUrl(`${baseUrl}/giao-dich-xac-minh`, {
                      gateway,
                      refId: data.order.id,
                      paymentQrCode: data.paymentQrCode,
                      paymentUrl: data.paymentUrl,
                    });
                    redirectToPaymentGateway(verifyUrl, data.order.id, gatewayPopup);
                  } else {
                    redirectToPaymentGateway(data.paymentUrl, data.order.id, gatewayPopup);
                  }
                  // Poll đơn ngay — khi MoMo mất opener vẫn hiện success trên SDK
                  if (!standalonePayment && data?.order?.id) {
                    startGatewayPoll(String(data.order.id));
                  }
                } catch (e: any) {
                  if (gatewayPopup && !gatewayPopup.closed) {
                    intentionalPopupCloseRef.current = true;
                    try {
                      gatewayPopup.close();
                    } catch {
                      /* ignore */
                    }
                    paymentPopupRef.current = null;
                  }
                  showError(e?.response?.data || e);
                  onPaymentError(e?.response?.data || e);
                } finally {
                  setSubmitting(false);
                  submittingRef.current = false;
                }
              }}
            >
              Thanh Toán Ngay
            </button>
          </div>
            </>
          )}
        </div>
      </> : (order.status === ORDER_STATUS.PAID || gatewayTrustedSuccess) ? <PaymentSuccess
        character={character}
        order={order}
        onClose={onClose}
        standalonePayment={standalonePayment} />
        : <PaymentCanceled
          character={character}
          order={order}
          onClose={onClose}
        />}
    </div>
  );
}
